/**
 * FP3-058 — Caseload bulk-select actions.
 *
 * One file because the three pieces share state, copy, and reason
 * lists:
 *   - BulkActionsToolbar — sticky bar showing selected count +
 *     Archive / Delete actions. Visible only when canMutate=true.
 *   - ArchiveSelectedModal — reason dropdown (server-fed) + optional
 *     note. POSTs to /api/students/bulk-archive.
 *   - DeleteSelectedModal — typed "DELETE" confirmation + reason
 *     dropdown (server-fed) + optional note. POSTs to
 *     /api/students/bulk-soft-delete.
 *
 * Permission model is enforced server-side per row; the toolbar only
 * gates the UI affordance. Solo provider owners + admin family see
 * the buttons; school-team providers + assistants do not.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Trash, X as XIcon, WarningCircle, GraduationCap } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";

const HARDCODED_EXIT_REASONS = [
  { key: "graduated", label: "Graduated" },
  { key: "withdrew", label: "Withdrew" },
  { key: "parent_revoked_services", label: "Parent revoked services" },
  { key: "moved_transferred", label: "Moved / transferred" },
  { key: "no_longer_eligible", label: "No longer eligible" },
  { key: "duplicate_or_test_record", label: "Duplicate or test record" },
  { key: "other", label: "Other" },
];

const HARDCODED_DELETION_REASONS = [
  { key: "duplicate_or_test_record", label: "Duplicate or test record" },
  { key: "accidental_import", label: "Accidental import" },
  { key: "created_by_mistake", label: "Created by mistake" },
  { key: "other", label: "Other" },
];

export function BulkActionsToolbar({
  selectedIds,
  onClearSelection,
  onArchive,
  onDelete,
  onAssignCampus,
}) {
  const count = selectedIds.length;
  if (count === 0) return null;
  return (
    <div
      data-testid="caseload-bulk-toolbar"
      className="sticky top-0 z-30 -mx-6 lg:-mx-8 px-6 lg:px-8 py-2.5 bg-[#1F2937] text-white flex items-center justify-between shadow-md mb-4"
    >
      <div className="flex items-center gap-3 text-[12.5px]">
        <span
          data-testid="caseload-bulk-count"
          className="font-semibold"
        >
          {count} selected
        </span>
        <button
          type="button"
          onClick={onClearSelection}
          data-testid="caseload-bulk-clear"
          className="text-[11px] text-[#D1D5DB] hover:text-white underline"
        >
          Clear
        </button>
      </div>
      <div className="flex items-center gap-2">
        {/* FP3-064 — Bulk reassign students to a school/campus.
            Surfaced for the same callers who already see Archive +
            Delete (admin family + solo provider OWNER). */}
        {onAssignCampus && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onAssignCampus}
            data-testid="caseload-bulk-assign-campus-btn"
            className="h-8 text-[12px] border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
          >
            <GraduationCap size={13} weight="bold" className="mr-1.5" />
            Assign campus
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onArchive}
          data-testid="caseload-bulk-archive-btn"
          className="h-8 text-[12px] border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
        >
          <Archive size={13} weight="bold" className="mr-1.5" />
          Archive selected
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={onDelete}
          data-testid="caseload-bulk-delete-btn"
          className="h-8 text-[12px] bg-[#9B2B2B] hover:bg-[#7A1F1F] text-white"
        >
          <Trash size={13} weight="bold" className="mr-1.5" />
          Delete selected
        </Button>
      </div>
    </div>
  );
}

export function ArchiveSelectedModal({
  open, onOpenChange, selectedIds, onSuccess,
}) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState(HARDCODED_EXIT_REASONS);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setNote("");
    // Pull the canonical list if the admin endpoint is reachable
    // (admin / org_admin paths). Solos fall back to the hardcoded
    // list since the admin endpoint requires admin-level auth.
    api.adminExitReasons()
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        if (list.length) setReasons(list);
      })
      .catch(() => {});
  }, [open]);

  const canSubmit = !!reason && selectedIds.length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await api.caseloadBulkArchiveStudents({
        student_ids: selectedIds,
        exit_reason: reason,
        exit_note: note.trim(),
      });
      const archived = r?.data?.archived_count || 0;
      const skipped = r?.data?.skipped_count || 0;
      if (skipped > 0) {
        toast.warning(`Archived ${archived}; ${skipped} skipped (permission or already archived).`);
      } else {
        toast.success(`Archived ${archived} student${archived === 1 ? "" : "s"}.`);
      }
      onSuccess?.(r?.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not archive selection");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="caseload-bulk-archive-modal"
        className="sm:max-w-md bg-white"
      >
        <DialogHeader>
          <DialogTitle className="text-[15px] flex items-center gap-2">
            <Archive size={16} weight="duotone" className="text-[#1E3F33]" />
            Archive {selectedIds.length} student{selectedIds.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Archive selected students? They will no longer appear in
            the active caseload, but their records and history will
            remain. Goals, notes, sessions, documents, and imported
            data are <span className="font-semibold">not erased</span>{" "}
            — nothing is hard-deleted. This only changes the archive
            visibility flag; you can find them later with the{" "}
            <span className="font-semibold">Archived</span> status
            filter.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label
              htmlFor="bulk-archive-reason"
              className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
            >
              Exit reason <span className="text-[#A8453A]">*</span>
            </label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger
                id="bulk-archive-reason"
                data-testid="caseload-bulk-archive-reason"
                className="mt-1"
              >
                <SelectValue placeholder="Pick a reason…" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem
                    key={r.key}
                    value={r.key}
                    data-testid={`caseload-bulk-archive-reason-${r.key}`}
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label
              htmlFor="bulk-archive-note"
              className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
            >
              Note (optional)
            </label>
            <Textarea
              id="bulk-archive-note"
              data-testid="caseload-bulk-archive-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional context — capped at 500 characters."
              maxLength={500}
              className="mt-1 text-[12.5px]"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="caseload-bulk-archive-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="caseload-bulk-archive-confirm"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white disabled:bg-[#1E3F33]/50"
          >
            {submitting ? "Archiving…" : "Archive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteSelectedModal({
  open, onOpenChange, selectedIds, onSuccess,
}) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState(HARDCODED_DELETION_REASONS);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setNote("");
    setConfirmText("");
    // Server-fed list (matches the backend taxonomy).
    api.caseloadDeletionReasons()
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        if (list.length) setReasons(list);
      })
      .catch(() => {});
  }, [open]);

  const typedOk = confirmText.trim().toUpperCase() === "DELETE";
  const canSubmit = !!reason && typedOk && selectedIds.length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await api.caseloadBulkSoftDeleteStudents({
        student_ids: selectedIds,
        deletion_reason: reason,
        deletion_note: note.trim(),
        confirm: "DELETE",
      });
      const deleted = r?.data?.deleted_count || 0;
      const skipped = r?.data?.skipped_count || 0;
      if (skipped > 0) {
        toast.warning(`Deleted ${deleted}; ${skipped} skipped (permission denied).`);
      } else {
        toast.success(`Deleted ${deleted} student${deleted === 1 ? "" : "s"}.`);
      }
      onSuccess?.(r?.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete selection");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="caseload-bulk-delete-modal"
        className="sm:max-w-md bg-white border-[#F4D1D1]"
      >
        <DialogHeader>
          <DialogTitle className="text-[15px] flex items-center gap-2 text-[#7A1F1F]">
            <WarningCircle size={16} weight="duotone" className="text-[#9B2B2B]" />
            Delete {selectedIds.length} student{selectedIds.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Move selected student{selectedIds.length === 1 ? "" : "s"} to
            deleted? This removes them from your active workspace view
            but does <span className="font-semibold">not permanently
            erase</span> student history. Goals, notes, sessions,
            documents, and history are preserved — no record is
            hard-deleted. This only changes the deletion visibility
            flag; a platform admin can still recover them later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-[#F4D1D1] bg-[#FBEFE8] px-3 py-2 text-[11.5px] text-[#7A3A2C] leading-relaxed">
            <span className="font-semibold">Heads up:</span> use Archive
            for normal student exits. Reserve Delete for accidental
            imports, duplicates, or test records.
          </div>
          <div>
            <label
              htmlFor="bulk-delete-reason"
              className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
            >
              Deletion reason <span className="text-[#A8453A]">*</span>
            </label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger
                id="bulk-delete-reason"
                data-testid="caseload-bulk-delete-reason"
                className="mt-1"
              >
                <SelectValue placeholder="Pick a reason…" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem
                    key={r.key}
                    value={r.key}
                    data-testid={`caseload-bulk-delete-reason-${r.key}`}
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label
              htmlFor="bulk-delete-note"
              className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
            >
              Note (optional)
            </label>
            <Textarea
              id="bulk-delete-note"
              data-testid="caseload-bulk-delete-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why are you deleting these? Capped at 500 characters."
              maxLength={500}
              className="mt-1 text-[12.5px]"
              rows={3}
            />
          </div>
          <div>
            <label
              htmlFor="bulk-delete-confirm"
              className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
            >
              Type <span className="font-mono text-[#9B2B2B]">DELETE</span> to confirm
              <span className="text-[#A8453A]"> *</span>
            </label>
            <Input
              id="bulk-delete-confirm"
              data-testid="caseload-bulk-delete-typed"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="mt-1 font-mono text-[12.5px]"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="caseload-bulk-delete-cancel"
          >
            <XIcon size={12} className="mr-1" /> Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="caseload-bulk-delete-confirm"
            className="bg-[#9B2B2B] hover:bg-[#7A1F1F] text-white disabled:bg-[#9B2B2B]/40 disabled:cursor-not-allowed"
          >
            <Trash size={12} weight="bold" className="mr-1.5" />
            {submitting ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
